'use client';

import { useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode, Mousewheel, A11y } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';

interface MustKnowMakersSectionProps {
  makers?: any[];
}

// Fallback makers data
const fallbackMakers = [
  {
    id: 1,
    backgroundImage: "https://images.unsplash.com/photo-1604076913837-52ab5629fba9?w=800&h=600&fit=crop&auto=format",
    profileImage: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=300&h=300&fit=crop&auto=format",
    location: "Berlin, Germany",
    name: "Holly Herndon",
    description: "Pioneering musician and AI researcher exploring the intersection of machine learning and human voice, creating groundbreaking electronic compositions.",
    tags: ["MUSIC", "GENERATIVE ART"],
    slug: "holly-herndon"
  },
  {
    id: 2,
    backgroundImage: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&h=600&fit=crop&auto=format",
    profileImage: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=300&fit=crop&auto=format",
    location: "Rio de Janeiro, Brazil",
    name: "Hanna Inaiáh",
    description: "Fashion designer revolutionizing sustainable fashion through AI-generated patterns and zero-waste design methodologies.",
    tags: ["FASHION", "IMAGE GENERATION"],
    slug: "hanna-inaiah"
  },
  {
    id: 3,
    backgroundImage: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop&auto=format",
    profileImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&auto=format",
    location: "London, United Kingdom",
    name: "Anna Ridler",
    description: "Artist and researcher working with machine learning and datasets, known for her tulip-based works exploring AI bias and creativity.",
    tags: ["IMAGE GENERATION"],
    slug: "anna-ridler"
  },
  {
    id: 4,
    backgroundImage: "https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=800&h=600&fit=crop&auto=format",
    profileImage: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=300&h=300&fit=crop&auto=format",
    location: "Istanbul, Turkey",
    name: "Refik Anadol",
    description: "Media artist creating immersive data sculptures and paintings using machine learning algorithms to process vast datasets.",
    tags: ["GENERATIVE ART"],
    slug: "refik-anadol"
  },
  {
    id: 5,
    backgroundImage: "https://images.unsplash.com/photo-1604079628040-94301bb21b91?w=800&h=600&fit=crop&auto=format",
    profileImage: "https://images.unsplash.com/photo-1594751543129-6701ad444259?w=300&h=300&fit=crop&auto=format",
    location: "Lisbon, Portugal",
    name: "Sofia Crespo",
    description: "Generative artist exploring artificial life, neural networks, and the boundaries between organic and synthetic forms.",
    tags: ["BOTANICAL", "IMAGE GENERATION"],
    slug: "sofia-crespo"
  }
];

export default function MustKnowMakersSection({ makers: propMakers = [] }: MustKnowMakersSectionProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  
  // Transform WordPress data or use fallback
  const getOptimizedImageUrl = (image: any, preferredWidth: number = 800) => {
    if (!image) return '';
    
    if (image.mediaDetails?.sizes && image.mediaDetails.sizes.length > 0) {
      const sizes = [...image.mediaDetails.sizes].sort((a, b) => 
        Math.abs(a.width - preferredWidth) - Math.abs(b.width - preferredWidth)
      );
      return sizes[0]?.sourceUrl || image.sourceUrl || image.mediaItemUrl || '';
    }
    
    return image.sourceUrl || image.mediaItemUrl || '';
  };

  const allMakers = propMakers && propMakers.length > 0 
    ? propMakers.map((maker, index) => {
        const fallbackMaker = fallbackMakers[index % fallbackMakers.length];
        
        return {
          id: parseInt(maker.id) || index + 1,
          backgroundImage: getOptimizedImageUrl(maker.backgroundImage, 800) || 
                          fallbackMaker?.backgroundImage || '',
          profileImage: getOptimizedImageUrl(maker.profileImage, 200) || 
                       fallbackMaker?.profileImage || '',
          location: maker.makerLocation || fallbackMaker?.location || '',
          name: maker.title || fallbackMaker?.name || '',
          description: maker.makerBio || maker.content || fallbackMaker?.description || '',
          tags: maker.makerSpecialties?.nodes?.map((spec: any) => spec.name).filter(Boolean) || fallbackMaker?.tags || [],
          slug: maker.slug || fallbackMaker?.slug || `maker-${index + 1}`,
          featured: maker.featuredMaker !== false,
          position: maker.featuredPosition || index
        };
      })
    : fallbackMakers.map((maker, index) => ({ ...maker, featured: true, position: index }));

  const makers = allMakers
    .filter(maker => maker.featured)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .slice(0, 10);

  return (
    <section className="w-full flex justify-center">
      <div className="relative w-full max-w-[1440px] border-t border-b border-[#000000] bg-white">
        {/* Header */}
        <div className="px-[7.5vw] sm:px-8 md:px-10 lg:px-12 pt-[12.5vw] sm:pt-16 md:pt-18 lg:pt-20">
          {/* Header */}
          <div className="flex items-baseline justify-between pb-[7.5vw] sm:pb-10 gap-4">
            <h2 className="text-[6.25vw] sm:text-[32px] lg:text-[36px] leading-[1] sm:leading-[42px] lg:leading-[48px] font-gilda-display font-normal whitespace-nowrap">
              Featured Makers
            </h2>
            <Link 
              href="/makers" 
              className="text-[3.75vw] sm:text-[13px] md:text-[14px] leading-[1] font-ibm-plex-sans-condensed font-medium tracking-[1.2px] sm:tracking-[1.3px] md:tracking-[1.4px] uppercase underline hover:no-underline sm:underline-offset-0 sm:no-underline sm:inline-flex sm:items-center sm:justify-center sm:px-5 sm:md:px-6 sm:py-2.5 sm:h-10 sm:border sm:border-black sm:hover:bg-black sm:hover:text-white transition-all"
            >
              ALL MAKERS
            </Link>
          </div>
        </div>

        {/* Swiper Container */}
        <div className="relative">
          <Swiper
            modules={[Navigation, FreeMode, Mousewheel, A11y]}
            spaceBetween={16}
            slidesPerView={'auto'}
            slidesOffsetBefore={24}
            slidesOffsetAfter={24}
            navigation={true}
            freeMode={{
              enabled: true,
              sticky: true,
              momentumRatio: 0.5,
              minimumVelocity: 0.02
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 0.5,
              releaseOnEdges: true
            }}
            watchSlidesProgress={true}
            speed={600}
            resistanceRatio={0.85}
            breakpoints={{
              640: {
                spaceBetween: 20,
                slidesOffsetBefore: 32,
                slidesOffsetAfter: 32,
              },
              768: {
                spaceBetween: 24,
                slidesOffsetBefore: 40,
                slidesOffsetAfter: 40,
              },
              1024: {
                spaceBetween: 24,
                slidesOffsetBefore: 48,
                slidesOffsetAfter: 48,
              },
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            className="pb-8"
          >
            {makers.map((maker, index) => (
              <SwiperSlide key={maker.id} style={{ width: 'auto' }}>
                <Link href={`/makers/${maker.slug}`} className="block bg-white flex flex-col w-[calc(100vw-48px)] sm:w-[320px] md:w-[340px] lg:w-[360px] overflow-hidden group cursor-pointer relative" prefetch={true}>
                  {/* Background Image with Profile */}
                  <div className="block">
                    <div className="h-[305px] sm:h-[300px] md:h-[320px] lg:h-[366px] relative overflow-hidden">
                      {maker.backgroundImage && (
                        <Image
                          src={maker.backgroundImage}
                          alt={`${maker.name} background`}
                          fill
                          sizes="(max-width: 640px) 280px, (max-width: 1024px) 320px, 362px"
                          className="object-cover swiper-lazy"
                          loading="lazy"
                          priority={false}
                        />
                      )}
                      <div className="swiper-lazy-preloader"></div>
                      
                      {/* Gradient overlay */}
                      <div 
                        className="absolute inset-0 z-[1]" 
                        style={{
                          backgroundImage: 'linear-gradient(172.665deg, rgba(0, 0, 0, 0) 55.997%, rgba(0, 0, 0, 0.4) 95.846%)'
                        }} 
                      />
                      
                      {/* Hover overlay - Desktop only */}
                      <div className="absolute inset-0 bg-black/0 lg:group-hover:bg-black/10 transition-all duration-300 z-[2]" />
                      
                      <div className="relative z-10 flex flex-col h-full justify-between p-3">
                        {/* Profile Image */}
                        <div className="flex justify-end">
                          <div className="relative w-[80px] h-[80px] sm:w-[91px] sm:h-[91px]">
                            {maker.profileImage && (
                              <Image
                                alt={`Profile of ${maker.name}`}
                                className="rounded-full object-cover"
                                src={maker.profileImage}
                                fill
                                sizes="(max-width: 640px) 80px, 91px"
                                priority={index < 3}
                              />
                            )}
                          </div>
                        </div>
                        
                        {/* Location and Name */}
                        <div className="text-white text-center pb-6">
                          <p className="font-ibm-plex-sans-condensed font-medium text-[10px] tracking-[1px] uppercase mb-3">
                            {maker.location}
                          </p>
                          <h3 className="text-[36px] font-gilda-display leading-[48px]">
                            {maker.name}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Container */}
                  <div className="flex flex-col flex-1 px-0 py-6">
                    {/* Description */}
                    <div className="flex-1 mb-6">
                      <p className="font-ibm-plex-sans text-[#000000] text-[16px] lg:text-[18px] leading-[26px] lg:leading-[28px]">
                        {maker.description}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {maker.tags.map((tag: string, tagIndex: number) => (
                        <div 
                          key={tagIndex} 
                          className="bg-[#f6f4f1] border border-[#f0ede8] rounded-full px-[12.53px] py-[8.354px] flex items-center"
                        >
                          <span className="font-ibm-plex-sans-condensed text-[#000000] text-[12px] tracking-[1px] uppercase leading-[14px]">
                            {tag}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>

        </div>
        
        {/* Bottom padding to match top */}
        <div className="pb-8 sm:pb-10 lg:pb-12"></div>
      </div>

      {/* Custom styles for Swiper */}
      <style jsx global>{`
        /* Navigation arrows - Hidden on touch devices, visible on hover for desktop */
        .swiper-button-prev,
        .swiper-button-next {
          opacity: 0;
          transition: opacity 0.3s ease;
          color: #000;
          width: 44px;
          height: 44px;
          /* Center on image height - desktop: 366px, arrow is 44px, so top = (366-44)/2 = 161px */
          top: 161px;
          margin-top: 0;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        
        /* Responsive positioning for different screen sizes */
        @media (max-width: 1023px) {
          .swiper-button-prev,
          .swiper-button-next {
            /* Medium screens: image height 320px, arrow is 44px, so top = (320-44)/2 = 138px */
            top: 138px;
          }
        }
        
        @media (max-width: 767px) {
          .swiper-button-prev,
          .swiper-button-next {
            /* Small screens: image height 300px, arrow is 44px, so top = (300-44)/2 = 128px */
            top: 128px;
          }
        }
        
        @media (max-width: 639px) {
          .swiper-button-prev,
          .swiper-button-next {
            /* Mobile: image height 305px, arrow is 44px, so top = (305-44)/2 = 130.5px */
            top: 130.5px;
          }
        }
        
        /* Hide arrows completely on tablets and mobile */
        @media (max-width: 1279px) {
          .swiper-button-prev,
          .swiper-button-next {
            display: none !important;
          }
        }
        
        /* Show navigation arrows on hover for desktop only */
        @media (min-width: 1280px) {
          .swiper:hover .swiper-button-prev:not(.swiper-button-disabled),
          .swiper:hover .swiper-button-next:not(.swiper-button-disabled) {
            opacity: 1;
          }
        }
        
        /* Keep disabled buttons hidden even on hover */
        .swiper-button-disabled {
          opacity: 0 !important;
          pointer-events: none;
        }
        
        /* Style the arrow icons */
        .swiper-button-prev:after,
        .swiper-button-next:after {
          font-size: 16px;
          font-weight: bold;
        }
        
        /* Hover effect on buttons */
        .swiper-button-prev:hover,
        .swiper-button-next:hover {
          background: #000;
          color: #fff;
        }
        
        .swiper-lazy-preloader {
          width: 42px;
          height: 42px;
          position: absolute;
          left: 50%;
          top: 50%;
          margin-left: -21px;
          margin-top: -21px;
          z-index: 10;
          transform-origin: 50%;
          animation: swiper-preloader-spin 1s infinite linear;
          box-sizing: border-box;
          border: 4px solid #000;
          border-radius: 50%;
          border-top-color: transparent;
        }
        
        @keyframes swiper-preloader-spin {
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
}